/**
 * OpenPanel client-side analytics. The snippet itself is injected into `<head>` by
 * the `hrms-openpanel` plugin in vite.config.ts, and only when
 * PUBLIC_OPENPANEL_CLIENT_ID was set at build time — so `window.op` is undefined in
 * an unconfigured build and every call here is a no-op.
 * Markup events need no code: `data-track="event_name"` on an element is picked up
 * by `trackAttributes`. Code-driven events go through `op()` below.
 */
import type { Me } from "@hrms/shared";

declare global {
  interface Window {
    op?: (method: string, ...args: unknown[]) => void;
  }
}

/**
 * Calls the OpenPanel queue. NEVER write `window.op?.(…)`: the production build
 * lowers an optional member call to `window.op.call(window, …)`, and on the snippet's
 * queue Proxy reading `.call` hits the `get` trap, which queues `["call", …]` instead
 * of the real method. op1.js then drops it as an unknown method, so identify was
 * silently lost in production while working in the unminified dev server.
 * Reading into a local and calling it plainly avoids any property access.
 */
function op(method: string, ...args: unknown[]): void {
  const queue = window.op;
  if (typeof queue === "function") queue(method, ...args);
}

/**
 * Ties subsequent events to the signed-in employee. `profileId` is the employee
 * UUID — stable across email changes — and the email rides along as a trait so the
 * dashboard shows who it is. Safe before op1.js loads: the queue proxy buffers it.
 */
export function identify(employee: Me["employee"]): void {
  op("identify", {
    profileId: employee.id,
    email: employee.workEmail,
    firstName: employee.name,
  });
}
